import { useCallback, useMemo } from 'react';
import { set } from 'lodash/fp';

import { LinkedDocument, ArkTableDocument } from '../components/documents/store/types';
import { getNettingDetermination } from '../components/opinions/netting-engine/store/utils';
import { NettingEngineSummary } from '../components/opinions/store/types';
import { dateFormatter } from '../components/shared/table/arkTableFormatters';
import { ArkTableColumn } from '../components/shared/table/types';
import { Action } from '../components/shared/modal/ActionModal';
import { useAppSelector } from './react-redux';
import { getUser } from '../components/auth/login/store/selectors';
import { Document } from '../components/shared/icons';
import { getAllBasicUsers } from '../components/admin/users/store';
import { initialDocumentColumnIds } from '../components/constants/documents';

export const useOriginalDocumentsColumnDefs = (actions: (data: ArkTableDocument) => Action[], hasOpinionModule: boolean) => {
    const user = useAppSelector(getUser);
    const allBasicUsers = useAppSelector(getAllBasicUsers);
    const userDocumentColumns = useMemo(() => (user && user.userDefaults.documentColumns) || initialDocumentColumnIds, [user]);
    const amendmentDocumentsFormatter = useCallback((documents: LinkedDocument[]) => documents.map(({ documentDescription }) => documentDescription), []);

    const nettingAnalysisFormatter = useCallback((nettingAnalysis: NettingEngineSummary | null, data: ArkTableDocument) => {
        const { colour, tooltip, isRecalculating } = getNettingDetermination(nettingAnalysis, data, hasOpinionModule);
        return [colour, tooltip, isRecalculating ? 'showSpinner' : ''];
    }, [hasOpinionModule]);

    const userFormatter = useCallback((rowUserId: number) => {
        if (rowUserId === user?.userId) {
            return 'Me';
        }
        const foundUser = allBasicUsers.find(({ userId }) => userId === rowUserId);
        if (foundUser) {
            return foundUser.forenames ? `${foundUser.forenames} ${foundUser.surname} (${foundUser.username})` : foundUser.username;
        }
        return '';
    }, [allBasicUsers, user]);

    const allColumnDefs: ArkTableColumn[] = useMemo(() => [
        { id: 'documentDescription', header: 'Document Description', field: 'documentDescription', width: 0.2, component: 'tooltip', canFilter: true, canSort: true },
        { id: 'documentName', header: 'Document', field: 'documentName', width: 0.2, component: 'tooltip', canFilter: true, canSort: true },
        { id: 'executedDate', header: 'Date of Execution', field: 'executedDate', width: 0.1, valueFormatter: dateFormatter, canSort: true },
        { id: 'datedAsOf', header: 'Dated as of', field: 'datedAsOf', width: 0.1, valueFormatter: dateFormatter, canSort: true },
        { id: 'entityA', header: 'Party A', field: 'entityA', width: 0.13, component: 'entity', canFilter: true, canSort: true },
        { id: 'entityB', header: 'Party B', field: 'entityB', width: 0.13, component: 'entity', canFilter: true, canSort: true },
        { id: 'allDocuments', header: 'All', field: 'allDocuments', width: 0.05, component: 'tooltipList', icon: Document, valueFormatter: amendmentDocumentsFormatter },
        { id: 'agency', header: 'Agency', field: 'isAgency', width: 0.05, component: 'tick' },
        { id: 'conApplicable', header: 'CON Applicable', field: 'nettingSummary', width: 0.05, component: 'nettingAnalysis', valueFormatter: nettingAnalysisFormatter },
        { id: 'instanceComplete', header: 'Completed', field: 'instanceComplete', width: 0.05, component: 'tick' },
        { id: 'createdBy', header: 'Uploaded By', field: 'createdBy', width: 0.13, component: 'tooltip', valueFormatter: userFormatter, canFilter: true, canSort: true },
        { id: 'modifiedBy', header: 'Last Modified By', field: 'modifiedBy', width: 0.13, component: 'tooltip', valueFormatter: userFormatter, canFilter: true, canSort: true },
        { id: 'actions', header: '', field: '', width: 0.04, component: 'action', actions }
    ], [actions, nettingAnalysisFormatter, amendmentDocumentsFormatter, userFormatter]);

    const widthModifier = useMemo(() => {
        const totalWidth = allColumnDefs.filter(({ id }) => userDocumentColumns.includes(id)).reduce((acc, cur) => acc + (cur.width as number), 0);
        return 1 / totalWidth;
    }, [userDocumentColumns, allColumnDefs]);

    // Need to readjust widths accordingly based on multiplier of total columns etc
    const customColumnDefs = useMemo(() => userDocumentColumns.map(columnId => allColumnDefs.find(({ id }) => id === columnId)!).map(column => set('width', (column.width as number) * widthModifier, column)), [allColumnDefs, userDocumentColumns, widthModifier]);
    return customColumnDefs;
};
